import { useRef, useState } from "react";
import { Map as MapIcon, PenTool, Square, Trash2, Check, X } from "lucide-react";

const input =
  "w-full bg-transparent border border-white/15 focus:border-gold outline-none px-4 py-3 text-sm text-[#FAF8F5] placeholder:text-[#626773] transition-colors";

const newId = () => "spot-" + Math.random().toString(36).slice(2, 8);

export default function AdminImageMap({ fiche, setFiche, allFiches }) {
  const wrapRef = useRef(null);
  const [mode, setMode] = useState(null); // 'poly' | 'rect'
  const [draft, setDraft] = useState([]);
  const [rectStart, setRectStart] = useState(null);
  const [selected, setSelected] = useState(null);
  const [imgUrl, setImgUrl] = useState("");
  const [editLayer, setEditLayer] = useState(null);

  const map = fiche.imagemap || null;
  const layers = map?.layers || [];
  const hasLayers = layers.length > 0;
  const baseLayerId = hasLayers ? (layers.find((l) => l.id === 0)?.id ?? layers[0].id) : 0;
  const currentLayer = editLayer ?? baseLayerId;
  const currentImage = hasLayers
    ? (layers.find((l) => l.id === currentLayer)?.image || map?.image)
    : map?.image;
  const allSpots = map?.spots || [];
  const spots = hasLayers ? allSpots.filter((s) => (s.layer ?? 0) === currentLayer) : allSpots;

  const setMap = (fn) => setFiche((f) => ({ ...f, imagemap: fn(f.imagemap) }));

  const toPct = (e) => {
    const r = wrapRef.current.getBoundingClientRect();
    return {
      x: Math.min(100, Math.max(0, ((e.clientX - r.left) / r.width) * 100)),
      y: Math.min(100, Math.max(0, ((e.clientY - r.top) / r.height) * 100)),
    };
  };

  const onCanvasClick = (e) => {
    if (mode !== "poly") return;
    const p = toPct(e);
    setDraft((d) => [...d, { x: Math.round(p.x * 10) / 10, y: Math.round(p.y * 10) / 10 }]);
  };

  const onMouseDown = (e) => {
    if (mode !== "rect") return;
    e.preventDefault();
    setRectStart(toPct(e));
  };

  const onMouseUp = (e) => {
    if (mode !== "rect" || !rectStart) return;
    const a = rectStart;
    const b = toPct(e);
    setRectStart(null);
    if (Math.abs(b.x - a.x) < 2 || Math.abs(b.y - a.y) < 2) return;
    finishShape([
      { x: a.x, y: a.y }, { x: b.x, y: a.y },
      { x: b.x, y: b.y }, { x: a.x, y: b.y },
    ].map((p) => ({ x: Math.round(p.x * 10) / 10, y: Math.round(p.y * 10) / 10 })));
  };

  const finishShape = (pts) => {
    const spot = {
      id: newId(),
      title: `Zone ${allSpots.length + 1}`,
      layer: currentLayer,
      goto: null,
      points: pts,
      tooltip_html: "",
      link: "",
    };
    setMap((m) => ({ ...m, spots: [...(m?.spots || []), spot] }));
    setSelected(spot.id);
    setDraft([]);
    setMode(null);
  };

  const updateSpot = (id, patch) =>
    setMap((m) => ({ ...m, spots: m.spots.map((s) => (s.id === id ? { ...s, ...patch } : s)) }));

  const removeSpot = (id) => {
    setMap((m) => ({ ...m, spots: m.spots.filter((s) => s.id !== id) }));
    if (selected === id) setSelected(null);
  };

  if (!map) {
    return (
      <div className="border border-white/10 p-6" data-testid="admin-imagemap">
        <label className="label-accent block mb-3">Image interactive (zones au survol)</label>
        <p className="mb-4 text-xs text-[#626773] leading-relaxed">
          Transformez une image (schéma, mindmap, contrat visuel…) en carte cliquable : le visiteur
          survole une zone et voit s'afficher le principe expliqué.
        </p>
        <div className="flex flex-col sm:flex-row gap-3">
          <input
            value={imgUrl}
            onChange={(e) => setImgUrl(e.target.value)}
            placeholder="URL de l'image (ex. /medias/mindmap.png)"
            className={input + " flex-1"}
            data-testid="admin-imagemap-url"
          />
          <button
            onClick={() => imgUrl.trim() && setFiche((f) => ({ ...f, imagemap: { image: imgUrl.trim(), layers: [], spots: [] } }))}
            className="border border-gold/40 text-gold text-xs uppercase tracking-[0.2em] px-5 py-3 hover:bg-gold/10 transition-colors shrink-0"
            data-testid="admin-imagemap-create"
          >
            Créer la carte
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="border border-white/10 p-6" data-testid="admin-imagemap">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <label className="label-accent flex items-center gap-2">
          <MapIcon className="w-4 h-4 text-gold/70" /> Image interactive ({allSpots.length} zone{allSpots.length > 1 ? "s" : ""}{hasLayers ? `, ${layers.length} calques` : ""})
        </label>
        <button
          onClick={() => setFiche((f) => ({ ...f, imagemap: null }))}
          className="text-red-400/70 hover:text-red-400 text-xs uppercase tracking-[0.15em] transition-colors"
          data-testid="admin-imagemap-delete"
        >
          Supprimer la carte
        </button>
      </div>

      {!hasLayers && (
        <input
          value={map.image}
          onChange={(e) => setMap((m) => ({ ...m, image: e.target.value }))}
          className={input + " mb-4"}
          placeholder="URL de l'image"
          data-testid="admin-imagemap-image"
        />
      )}

      {hasLayers && (
        <div className="flex flex-wrap gap-2 mb-4" data-testid="admin-imagemap-layers">
          {layers.map((l) => (
            <button
              key={l.id}
              onClick={() => { setEditLayer(l.id); setSelected(null); setMode(null); setDraft([]); }}
              className={`text-[10px] uppercase tracking-[0.2em] px-3 py-2 border transition-colors ${
                currentLayer === l.id ? "bg-gold text-ink border-gold" : "border-white/15 text-[#A0A5B1] hover:text-gold"
              }`}
              data-testid={`admin-imagemap-layer-${l.id}`}
            >
              {l.title}
            </button>
          ))}
        </div>
      )}

      <div className="flex flex-wrap gap-3 mb-3">
        <button
          onClick={() => { setMode(mode === "poly" ? null : "poly"); setDraft([]); }}
          className={`inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] px-4 py-2.5 border transition-colors ${
            mode === "poly" ? "bg-gold text-ink border-gold" : "border-gold/40 text-gold hover:bg-gold/10"
          }`}
          data-testid="admin-imagemap-mode-poly"
        >
          <PenTool className="w-3.5 h-3.5" /> Polygone
        </button>
        <button
          onClick={() => { setMode(mode === "rect" ? null : "rect"); setDraft([]); }}
          className={`inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] px-4 py-2.5 border transition-colors ${
            mode === "rect" ? "bg-gold text-ink border-gold" : "border-gold/40 text-gold hover:bg-gold/10"
          }`}
          data-testid="admin-imagemap-mode-rect"
        >
          <Square className="w-3.5 h-3.5" /> Rectangle
        </button>
        {mode === "poly" && (
          <>
            <button
              onClick={() => draft.length >= 3 && finishShape(draft)}
              disabled={draft.length < 3}
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] px-4 py-2.5 border border-emerald-400/50 text-emerald-400 hover:bg-emerald-400/10 transition-colors disabled:opacity-40"
              data-testid="admin-imagemap-finish"
            >
              <Check className="w-3.5 h-3.5" /> Terminer ({draft.length} pts)
            </button>
            <button
              onClick={() => { setDraft([]); setMode(null); }}
              className="inline-flex items-center gap-2 text-xs px-4 py-2.5 text-[#626773] hover:text-red-400 transition-colors"
              data-testid="admin-imagemap-cancel"
            >
              <X className="w-3.5 h-3.5" /> Annuler
            </button>
          </>
        )}
      </div>
      <p className="mb-3 text-xs text-[#626773]">
        {mode === "poly" && "Cliquez point par point sur l'image, puis « Terminer »."}
        {mode === "rect" && "Cliquez-glissez sur l'image pour tracer un rectangle."}
        {!mode && "Choisissez un outil de dessin, ou cliquez une zone existante pour l'éditer."}
      </p>

      <div
        ref={wrapRef}
        className={`relative bg-[#F5F0E6] border border-white/10 ${mode ? "cursor-crosshair" : ""}`}
        onClick={onCanvasClick}
        onMouseDown={onMouseDown}
        onMouseUp={onMouseUp}
        data-testid="admin-imagemap-canvas"
      >
        <img src={currentImage} alt="Carte" className="w-full h-auto block" draggable={false} />
        <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-0 w-full h-full">
          {spots.map((s) => (
            <polygon
              key={s.id}
              points={s.points.map((p) => `${p.x},${p.y}`).join(" ")}
              data-testid={`admin-imagemap-spot-${s.id}`}
              className={`transition-all duration-150 ${
                selected === s.id
                  ? "fill-[#C5A059]/35 stroke-[#9A7B3E]"
                  : "fill-[#C5A059]/10 stroke-[#C5A059]/70 hover:fill-[#C5A059]/25"
              } ${mode ? "pointer-events-none" : "cursor-pointer"}`}
              style={{ vectorEffect: "non-scaling-stroke" }}
              strokeWidth="1.5"
              onClick={() => !mode && setSelected(selected === s.id ? null : s.id)}
            />
          ))}
          {mode === "poly" && draft.length > 0 && (
            <polyline
              points={draft.map((p) => `${p.x},${p.y}`).join(" ")}
              className="fill-[#C5A059]/20 stroke-gold"
              style={{ vectorEffect: "non-scaling-stroke" }}
              strokeWidth="1.5"
              strokeDasharray="3 2"
            />
          )}
        </svg>
      </div>

      <div className="mt-4 space-y-3">
        {spots.map((s) => (
          <div
            key={s.id}
            className={`border p-4 ${selected === s.id ? "border-gold/50" : "border-white/10"}`}
            data-testid={`admin-imagemap-edit-${s.id}`}
          >
            <div className="flex flex-wrap gap-3 items-center">
              <input
                value={s.title}
                onChange={(e) => updateSpot(s.id, { title: e.target.value })}
                className={input + " flex-1 min-w-40"}
                placeholder="Titre de la zone"
                data-testid={`admin-imagemap-title-${s.id}`}
              />
              <select
                value={s.link || ""}
                onChange={(e) => updateSpot(s.id, { link: e.target.value })}
                className={input + " bg-ink cursor-pointer sm:w-56"}
                data-testid={`admin-imagemap-link-${s.id}`}
              >
                <option value="" className="bg-ink">Pas de lien</option>
                {(allFiches || []).map((f) => (
                  <option key={`${f.type}/${f.slug}`} value={`/ressources/${f.type}/${f.slug}`} className="bg-ink">
                    {f.title} ({f.type})
                  </option>
                ))}
              </select>
              {hasLayers && (
                <select
                  value={s.goto ?? ""}
                  onChange={(e) => updateSpot(s.id, { goto: e.target.value === "" ? null : Number(e.target.value) })}
                  className={input + " bg-ink cursor-pointer sm:w-52"}
                  data-testid={`admin-imagemap-goto-${s.id}`}
                >
                  <option value="" className="bg-ink">Clic = infobulle</option>
                  {layers.filter((l) => l.id !== currentLayer).map((l) => (
                    <option key={l.id} value={l.id} className="bg-ink">Clic → {l.title}</option>
                  ))}
                </select>
              )}
              <button
                onClick={() => removeSpot(s.id)}
                className="text-red-400/70 hover:text-red-400 transition-colors"
                aria-label="Supprimer la zone"
                data-testid={`admin-imagemap-remove-${s.id}`}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
            <textarea
              value={s.tooltip_html}
              onChange={(e) => updateSpot(s.id, { tooltip_html: e.target.value })}
              className={input + " mt-3 font-mono text-xs min-h-[70px]"}
              placeholder="Contenu de l'infobulle (le « principe » — HTML simple accepté : <p>, <strong>, <ul>…)"
              data-testid={`admin-imagemap-tooltip-${s.id}`}
            />
          </div>
        ))}
        {spots.length === 0 && (
          <p className="text-xs text-[#626773] italic">Aucune zone sur ce calque — dessinez-en une avec les outils ci-dessus.</p>
        )}
      </div>
      <p className="mt-3 text-xs text-[#626773]">N'oubliez pas d'enregistrer la fiche pour publier les changements.</p>
    </div>
  );
}
